// src/hooks/useConflictResolution.js
// React hook for conflict-aware booking operations

import { useCallback, useEffect, useState } from 'react';
import { ConflictResolutionService } from '../services/ConflictResolutionService';
import { BookingConflictEnhancer } from '../services/enhancers/BookingConflictEnhancer';
import { BookingLockManager } from '../utils/BookingLockManager';
import { TimeSlotValidator } from '../utils/TimeSlotValidator';
import { useAuth } from './useAuth';

export const useConflictResolution = (options = {}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lockInfo, setLockInfo] = useState(null);
  const [conflictStats, setConflictStats] = useState(null);

  // Configuration
  const conflictResolutionEnabled = options.enabled !== false && 
    ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED;
  
  // ==========================================
  // CORE CONFLICT RESOLUTION HOOKS
  // ==========================================

  /**
   * Create booking with conflict resolution
   */
  const createBookingWithConflictResolution = useCallback(async (bookingData, options = {}) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const enhancedOptions = {
        ...options,
        useConflictResolution: conflictResolutionEnabled,
        source: 'react_hook'
      };

      const result = await BookingConflictEnhancer.createBooking(
        user.businessId,
        bookingData,
        enhancedOptions
      );

      if (!result.success && result.conflicts) {
        // Return conflict information for UI handling
        return {
          ...result,
          hasConflicts: true,
          conflictCount: result.conflicts.length
        };
      }

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId, conflictResolutionEnabled]);

  /**
   * Update booking with conflict checking
   */
  const updateBookingWithConflictResolution = useCallback(async (customerId, bookingId, updateData, options = {}) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const enhancedOptions = {
        ...options,
        useConflictResolution: conflictResolutionEnabled
      };

      const result = await BookingConflictEnhancer.updateBooking(
        user.businessId,
        customerId,
        bookingId,
        updateData,
        enhancedOptions
      );

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId, conflictResolutionEnabled]);

  /**
   * Check time slot availability
   */
  const checkTimeSlotAvailability = useCallback(async (dateTime, duration = 60, excludeBookingId = null) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const result = await ConflictResolutionService.checkTimeSlotAvailability(
        user.businessId,
        dateTime,
        duration,
        excludeBookingId
      );

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  /**
   * Validate booking request
   */
  const validateBookingRequest = useCallback(async (bookingData) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const result = await ConflictResolutionService.validateBookingRequest(
        user.businessId,
        bookingData
      );

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  /**
   * Suggest alternative time slots
   */
  const suggestAlternativeTimeSlots = useCallback(async (date, duration = 60, count = 5) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const alternatives = await ConflictResolutionService.suggestAlternativeTimeSlots(
        user.businessId,
        date,
        duration,
        count
      );

      return alternatives;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  // ==========================================
  // TIME SLOT VALIDATION HOOKS
  // ==========================================

  /**
   * Validate individual time slot
   */
  const validateTimeSlot = useCallback((dateTime, duration = 60, businessHours = null) => {
    try {
      setError(null);
      
      const validation = TimeSlotValidator.validateTimeSlot(dateTime, duration, businessHours);
      
      return validation;

    } catch (error) {
      setError(error.message);
      return {
        isValid: false,
        errors: [error.message],
        warnings: [],
        info: []
      };
    }
  }, []);

  /**
   * Generate available time slots for a date
   */
  const generateAvailableTimeSlots = useCallback((date, duration = 60, businessHours = null, interval = 30) => {
    try {
      setError(null);
      
      const slots = TimeSlotValidator.generateAvailableTimeSlots(
        date,
        duration,
        businessHours,
        interval
      );
      
      return slots;

    } catch (error) {
      setError(error.message);
      return {
        success: false,
        error: error.message,
        slots: []
      };
    }
  }, []);

  /**
   * Get next available time slot
   */
  const getNextAvailableSlot = useCallback((currentDateTime, duration = 60, businessHours = null) => {
    try {
      setError(null);
      
      const nextSlot = TimeSlotValidator.getNextAvailableSlot(
        currentDateTime,
        duration,
        businessHours
      );
      
      return nextSlot;

    } catch (error) {
      setError(error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }, []);

  // ==========================================
  // LOCK MANAGEMENT HOOKS
  // ==========================================

  /**
   * Acquire time slot lock
   */
  const acquireTimeSlotLock = useCallback(async (timeSlot, options = {}) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const lock = await BookingLockManager.acquireLock(
        user.businessId,
        timeSlot,
        {
          ...options,
          source: 'react_hook'
        }
      );

      if (lock.success) {
        setLockInfo(lock);
      }

      return lock;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  /**
   * Release current lock
   */
  const releaseLock = useCallback(async () => {
    if (!lockInfo) {
      return { success: true, message: 'No active lock to release' };
    }

    try {
      setLoading(true);
      setError(null);

      const result = await BookingLockManager.releaseLock(lockInfo);
      
      if (result.success) {
        setLockInfo(null);
      }

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [lockInfo]);

  /**
   * Validate current lock
   */
  const validateCurrentLock = useCallback(async () => {
    if (!lockInfo) {
      return { isValid: false, reason: 'No active lock' };
    }

    try {
      const validation = await BookingLockManager.validateLock(lockInfo);
      
      if (!validation.isValid) {
        setLockInfo(null); // Clear invalid lock
      }

      return validation;

    } catch (error) {
      setError(error.message);
      return {
        isValid: false,
        reason: 'Validation failed',
        error: error.message
      };
    }
  }, [lockInfo]);

  // ==========================================
  // BATCH OPERATIONS HOOKS
  // ==========================================

  /**
   * Validate multiple bookings for conflicts
   */
  const validateMultipleBookings = useCallback(async (bookingsArray) => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const result = await BookingConflictEnhancer.validateBookingConflicts(
        user.businessId,
        bookingsArray
      );

      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  // ==========================================
  // STATISTICS AND MONITORING HOOKS
  // ==========================================

  /**
   * Load conflict resolution statistics
   */
  const loadConflictStats = useCallback(async (timeframe = 'week') => {
    if (!user?.businessId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const stats = await BookingConflictEnhancer.getConflictResolutionStats(
        user.businessId,
        timeframe
      );

      if (stats.success) {
        setConflictStats(stats);
      }

      return stats;

    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  /**
   * Get lock statistics
   */
  const getLockStatistics = useCallback(async () => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      const stats = await BookingLockManager.getLockStatistics(user.businessId);
      return stats;

    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [user?.businessId]);

  // ==========================================
  // MAINTENANCE HOOKS
  // ==========================================

  /**
   * Clean up expired locks
   */
  const cleanupExpiredLocks = useCallback(async () => {
    if (!user?.businessId) {
      throw new Error('No business ID available');
    }

    try {
      setLoading(true);
      setError(null);

      const result = await BookingLockManager.cleanupExpiredLocks(user.businessId);
      return result;

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  // ==========================================
  // EFFECT HOOKS
  // ==========================================

  /**
   * Auto-release lock on component unmount
   */
  useEffect(() => {
    return () => {
      if (lockInfo) {
        BookingLockManager.releaseLock(lockInfo).catch(console.warn);
      }
    };
  }, [lockInfo]);

  /**
   * Load initial conflict stats
   */
  useEffect(() => {
    if (user?.businessId && conflictResolutionEnabled && options.loadStatsOnMount !== false) {
      loadConflictStats().catch(console.warn);
    }
  }, [user?.businessId, conflictResolutionEnabled, loadConflictStats, options.loadStatsOnMount]);

  // ==========================================
  // RETURN HOOK API
  // ==========================================

  return {
    // State
    loading,
    error,
    lockInfo,
    conflictStats,
    
    // Configuration
    conflictResolutionEnabled,
    
    // Core conflict resolution methods
    createBookingWithConflictResolution,
    updateBookingWithConflictResolution,
    checkTimeSlotAvailability,
    validateBookingRequest,
    suggestAlternativeTimeSlots,
    
    // Time slot validation methods
    validateTimeSlot,
    generateAvailableTimeSlots,
    getNextAvailableSlot,
    
    // Lock management methods
    acquireTimeSlotLock,
    releaseLock,
    validateCurrentLock,
    
    // Batch operations
    validateMultipleBookings,
    
    // Statistics and monitoring
    loadConflictStats,
    getLockStatistics,
    
    // Maintenance
    cleanupExpiredLocks,
    
    // Utility methods
    clearError: () => setError(null),
    refreshStats: () => loadConflictStats()
  };
};